import { useCallback, useEffect, useRef, useState } from "react";
import { useAuth } from "react-oidc-context";
import { useScoreApi, unwrap } from "../api/scoreApi";
import { classes as classesCache, rides as ridesCache, scribeSeats } from "../local/db";
import { getManualRideById } from "../store/manualRides";
import { scribePositionsFor } from "../util/scribeAssignment";
import { useScoreContext } from "../context/ScoreContext";

// The seat a scribe chose in a multi-seat class, kept per class so a reload
// mid-ride doesn't drop them back into the other judge's sheet.
const choiceKey = (classId) => `scribe-fe.position.${classId}`;

function readChoice(classId) {
  if (!classId) return null;
  try {
    return sessionStorage.getItem(choiceKey(classId));
  } catch {
    return null;
  }
}

function writeChoice(classId, position) {
  if (!classId) return;
  try {
    sessionStorage.setItem(choiceKey(classId), position);
  } catch {
    // sessionStorage unavailable (private browsing) — the choice just won't
    // survive a reload.
  }
}

/**
 * #627 — resolve the judge position this scribe is scoring a ride at, from the
 * ride's class (Class.scribeAssignments, #396). Cache-first so it works offline;
 * revalidated from score-api when online.
 *
 * Returns { position, positions, choosePosition, loading, noLocalRecord }:
 *  - one assignment  → `position` is it, `positions` has one entry.
 *  - none            → `position` is null (the scribe isn't on this class).
 *  - two or more     → `position` is the chosen seat (persisted per class), and
 *                      `positions` lists the seats so the caller can offer them.
 *
 * #2361 — `positions: []` is ambiguous on its own: it means either "the class
 * IS known locally and this scribe genuinely has no seat on it" or "the class
 * was never resolved locally at all (offline, cold cache), so nothing is
 * actually known either way". `noLocalRecord` disambiguates: true only in the
 * second case (settled with no local/fresh class data ever obtained), false
 * once any resolution actually returned class data (even on an earlier
 * settle this hook is keeping via `keepLocal`, see settledKey below).
 * Callers must not report "not assigned" off noLocalRecord — see
 * ScoringForm's verifyStillAssignedScribe / notAssignedInfo.
 *
 * #2361 — a class-cache miss is not necessarily an assignment-unknown: the
 * `scribeSeats` store (local/db.js) durably records this scribe's own
 * classId+position wherever it's ever been observed online (login,
 * ClassList), independent of the (possibly cold) full class row. When the
 * class itself misses but `scribeSeats` has this scribe's seat for its
 * classId (known from the RIDE cache — see classFor below), that record
 * settles `positions`/`position` directly and `noLocalRecord` stays false:
 * the app DOES know the assignment, just not the full class. Only a
 * scribeSeats miss too keeps the honest noLocalRecord:true "can't confirm"
 * outcome. This depends on the ride itself being cached (classFor resolves
 * classId off ridesCache.get(rideId) first) — a rideId this device has never
 * seen before (a deep link, or an unhydrated ride) has no classId to look
 * the seat up by, and stays noLocalRecord:true. Out of scope here: normal
 * navigation (ClassList → ride) always caches the ride before this screen is
 * reached (see useRidesForClass), so this residual is a cold deep-link only.
 */
export function useAssignedPosition(rideId) {
  const api = useScoreApi();
  const { user } = useAuth();
  const { online } = useScoreContext();
  const sub = user?.profile?.sub ?? null;
  const [state, setState] = useState({
    loading: true,
    classId: null,
    positions: [],
    noLocalRecord: false,
  });
  const [chosen, setChosen] = useState(null);
  // #1888 — the key of the last run of this effect that actually settled
  // (any of the setState(loading:false, ...) calls below fired). Lets a
  // same-ride re-run (deps flap: `online` flips on a reconnect drain, or a
  // real venue-wifi blip) tell itself apart from a genuine ride switch, the
  // same way useScoreSheet's load effect does — see that hook's #1888
  // comment. ScoringForm gates its whole tree behind `if (loading ||
  // resolvingPosition) return <spinner>`, and resolvingPosition depends on
  // this hook's `loading`, so unconditionally flipping it true on every
  // effect run unmounts the scoring form on every connectivity blip: same
  // remount, same scroll reset / focus loss (#153) / visible flash (#154)
  // as the useScoreSheet cause, just via the OTHER half of that OR gate.
  const settledKey = useRef(null);

  useEffect(() => {
    if (!rideId) {
      setState({ loading: false, classId: null, positions: [], noLocalRecord: false });
      settledKey.current = null;
      return undefined;
    }
    let cancelled = false;
    const posKey = `${rideId}::${sub}`;
    const keepLocal = settledKey.current === posKey;
    if (!keepLocal) {
      setState((s) => ({ ...s, loading: true }));
    }

    const classFor = async (fetchClass) => {
      const manual = getManualRideById(rideId);
      const ride = manual ?? (await fetchClass.ride());
      const classId = ride?.classId ?? null;
      if (!classId) return { classId: null, klass: null };
      const klass = await fetchClass.klass(classId);
      return { classId, klass };
    };

    // #2361 — the scribeSeats fallback: only meaningful when the class row
    // itself missed (a hit already produced real positions above) AND a
    // classId is actually known (see the classFor comment on the hook doc —
    // a cold ride cache leaves classId null too, and there's nothing to look
    // the seat up by). Returns the settle-state to use, or null when
    // scribeSeats has no record either — the caller keeps its existing
    // noLocalRecord:true handling in that case.
    const seatFallback = async (classId) => {
      if (!classId || !sub) return null;
      let seat;
      try {
        seat = await scribeSeats.get(classId, sub);
      } catch {
        return null;
      }
      if (!seat?.positions?.length) return null;
      return { classId, positions: seat.positions, noLocalRecord: false };
    };

    (async () => {
      let resolved = null;
      try {
        resolved = await classFor({
          ride: () => ridesCache.get(rideId),
          klass: (id) => classesCache.get(id),
        });
      } catch {
        resolved = null;
      }
      if (!cancelled && resolved?.klass) {
        settledKey.current = posKey;
        setState({
          loading: false,
          classId: resolved.classId,
          positions: scribePositionsFor(resolved.klass, sub),
          noLocalRecord: false,
        });
        setChosen(readChoice(resolved.classId));
      }

      if (!online) {
        if (!resolved?.klass) {
          // #2361 — the class row itself is a miss, but that scribeSeats
          // record (durable, written independent of the class row — see
          // useScribeAssignments/useClassesForShow) may still say who this
          // scribe is on this classId. Only a seatFallback miss too falls
          // back to the honest "nothing known either way" noLocalRecord:true.
          const seat = await seatFallback(resolved?.classId ?? null);
          if (cancelled) return;
          settledKey.current = posKey;
          if (seat) {
            setState({ loading: false, ...seat });
            setChosen(readChoice(seat.classId));
          } else {
            setState({
              loading: false,
              classId: resolved?.classId ?? null,
              positions: [],
              noLocalRecord: true,
            });
          }
        }
        return;
      }

      try {
        const fresh = await classFor({
          ride: () => api.rides.getRide(rideId).then(unwrap),
          klass: (id) => api.classes.getClass(id).then(unwrap),
        });
        if (cancelled) return;
        settledKey.current = posKey;
        setState({
          loading: false,
          classId: fresh.classId,
          positions: scribePositionsFor(fresh.klass, sub),
          noLocalRecord: false,
        });
        setChosen(readChoice(fresh.classId));
      } catch {
        // Network failed after a cache hit — keep what we have; otherwise
        // settle out of loading with no assignment. #2361 — that "otherwise"
        // is also a cache miss (never resolved locally OR fresh), so it
        // gets the same scribeSeats fallback as the offline branch above
        // before falling back to noLocalRecord:true.
        if (!resolved?.klass) {
          const seat = await seatFallback(resolved?.classId ?? null);
          if (cancelled) return;
          settledKey.current = posKey;
          if (seat) {
            setState({ loading: false, ...seat });
            setChosen(readChoice(seat.classId));
          } else {
            setState({ loading: false, classId: null, positions: [], noLocalRecord: true });
          }
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [api, rideId, sub, online]);

  const choosePosition = useCallback(
    (position) => {
      writeChoice(state.classId, position);
      setChosen(position);
    },
    [state.classId],
  );

  const { positions } = state;
  const position =
    positions.length === 1
      ? positions[0]
      : chosen && positions.includes(chosen)
        ? chosen
        : null;

  return {
    position,
    positions,
    choosePosition,
    loading: state.loading,
    noLocalRecord: state.noLocalRecord,
  };
}
